import React from "react";
import { FormattedMessage } from "@/i18n/i18n-client";
import EditNameModal from "./EditNameModal";
import EditEmailModal from "./EditEmailModal";
import EditPasswordModal from "./EditPasswordModal";
import EditPhoneModal from "./EditPhoneModal";
import type { FormValues, ModeType, SuccessStateType } from "./types";
import { Modes } from "./types";
import { OutlinedEditIcon } from "@/assets/Icons/OutlinedEditIcon";
import { useUpdateCustomerInfoMutation } from "@/generated/graphql";
import { useAuth } from "@/lib/Authentication/hooks/useAuth";
import { useStore } from "@/lib/storeData/StoreContext";
import { createDefaultPhoneInput } from "@/shared/globals/UiElements/InputPhone";
import Button from "../../Button/Button";

interface EditUserInfoProps {
  mode: ModeType;
  onSuccess: ({ mode, isSuccess }: SuccessStateType) => void;
}

const EditUserInfo = ({ mode, onSuccess }: EditUserInfoProps) => {
  const { storeId } = useStore();
  const [isOpen, setIsOpen] = React.useState(false);
  const [error, setError] = React.useState<React.ReactNode>(null);
  const { user, updateUser } = useAuth();

  const phoneNumber = createDefaultPhoneInput(user?.phone);

  const [updateCustomer] = useUpdateCustomerInfoMutation();

  const handleSubmit = async (values: FormValues, mode: ModeType) => {
    try {
      await updateCustomer({
        variables: {
          customerId: user?.id!,
          storeId,
          input: {
            name: values?.name,
            phone: values?.phone?.value,
            email: values?.email,
            password: values?.password,
            currentPassword: values?.currentPassword,
          },
        },
        onCompleted: (res) => {
          updateUser({
            email: res?.updateCustomer?.email,
            name: res?.updateCustomer?.name,
            phone: res?.updateCustomer?.phone,
          });
        },
      });
      setIsOpen(false);
      setError(null);
      onSuccess({ mode, isSuccess: true });
    } catch (e) {
      setError(
        <FormattedMessage defaultMessage="Something Went Wrong!!" id="8MdaTS" />
      );
      if (e.message === "Wrong Password") {
        setError(
          <FormattedMessage defaultMessage="Wrong Password" id="sVuTur" />
        );
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setError(null);
  };

  return (
    <>
      <Button
        prefixIcon={<OutlinedEditIcon />}
        onClick={() => setIsOpen(true)}
        type="secondary"
        size="xs"
      >
        <FormattedMessage defaultMessage="Edit" id="wEQDC6" />
      </Button>
      {mode === Modes["NAME"] ? (
        <EditNameModal
          isOpen={isOpen}
          onClose={handleClose}
          name={user?.name!}
          onSubmit={(values) => handleSubmit(values, mode)}
          error={error}
        />
      ) : mode === Modes["EMAIL"] ? (
        <EditEmailModal
          isOpen={isOpen}
          onClose={handleClose}
          email={user?.email!}
          onSubmit={(values) => handleSubmit(values, mode)}
          error={error}
        />
      ) : mode === Modes["PASSWORD"] ? (
        <EditPasswordModal
          isOpen={isOpen}
          onClose={handleClose}
          onSubmit={(values) => handleSubmit(values, mode)}
          error={error}
        />
      ) : mode === Modes["PHONE"] ? (
        <EditPhoneModal
          isOpen={isOpen}
          onClose={handleClose}
          phone={phoneNumber!}
          onSubmit={(values) => handleSubmit(values, mode)}
          error={error}
        />
      ) : null}
    </>
  );
};

export default EditUserInfo;
